// Strategies list with imperative reload + automatic refresh after mutating actions.
// Keeps state local to this hook instance so each page can have its own view; the
// shared "which strategy is selected" state lives in SelectedStrategyContext.

import { useCallback, useEffect, useState } from "react";
import { listStrategies } from "../api/strategies";
import type { StrategyDto } from "../api/types";

export interface StrategiesState {
  strategies: StrategyDto[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useStrategies(autoRefreshMs?: number): StrategiesState {
  const [strategies, setStrategies] = useState<StrategyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listStrategies();
      setStrategies(result);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    if (!autoRefreshMs) return;
    const t = window.setInterval(reload, autoRefreshMs);
    return () => window.clearInterval(t);
  }, [reload, autoRefreshMs]);

  return { strategies, loading, error, reload };
}
